/**
 * Regular expressions for extracting route parameters from URL paths.
 * These patterns are used to match specific route formats and extract relevant data.
 */

/**
 * Matches payment plan routes with optional status suffix.
 *
 * Pattern: /^\/([0-3])\/payment-plan(-status)?\/([^/]+)(?:\/.*)?$/
 *
 * Captures:
 * - Group 1: Region identifier (0, 1, 2, or 3)
 * - Group 2: Optional "-status" suffix (if present)
 * - Group 3: Payment plan code/identifier
 *
 * Example matches:
 * - /0/payment-plan/ABC123 → ['0', undefined, 'ABC123']
 * - /1/payment-plan-status/XYZ789 → ['1', '-status', 'XYZ789']
 * - /2/payment-plan/CODE123/additional/path → ['2', undefined, 'CODE123']
 * - /3/payment-plan/BRL456 → ['3', undefined, 'BRL456']
 */
const paymentPlanRegex = /^\/([0-3])\/payment-plan(-status)?\/([^/]+)(?:\/.*)?$/

/**
 * Matches payment arrangement routes.
 *
 * Pattern: /^\/([0-3])\/payment-arrangement\/([^/]+)(?:\/.*)?$/
 *
 * Captures:
 * - Group 1: Region identifier (0, 1, 2, or 3)
 * - Group 2: Payment arrangement code/identifier
 *
 * Example matches:
 * - /0/payment-arrangement/ABC123 → ['0', 'ABC123']
 * - /1/payment-arrangement/XYZ789/manage → ['1', 'XYZ789']
 * - /3/payment-arrangement/BRL456 → ['3', 'BRL456']
 */
const paymentArrangementRegex = /^\/([0-3])\/payment-arrangement\/([^/]+)(?:\/.*)?$/

/**
 * Matches secure code routes in two supported formats.
 *
 * Pattern: /^\/([A-Z0-9]-[A-Z0-9]{4}-[A-Z0-9]{4}|[A-Z0-9]{4}-[A-Z0-9]{4})(?:\/.*)?$/
 *
 * Captures:
 * - Group 1: Secure code in one of two formats:
 *   - Format 1: X-XXXX-XXXX (1 char, dash, 4 chars, dash, 4 chars)
 *   - Format 2: XXXX-XXXX (4 chars, dash, 4 chars)
 *
 * Example matches:
 * - /A-1234-5678 → ['A-1234-5678']
 * - /1234-5678/additional/path → ['1234-5678']
 * - /Z-ABCD-9999 → ['Z-ABCD-9999']
 */
const secureCodeRegex = /^\/([A-Z0-9]-[A-Z0-9]{4}-[A-Z0-9]{4}|[A-Z0-9]{4}-[A-Z0-9]{4})(?:\/.*)?$/

/**
 * Matches opt-out routes with two required parameters.
 *
 * Pattern: /^\/opt-out\/([^/]+)\/([^/]+)(?:\/.*)?$/
 *
 * Captures:
 * - Group 1: First opt-out parameter (typically customer/account identifier)
 * - Group 2: Second opt-out parameter (typically opt-out token/code)
 *
 * Example matches:
 * - /opt-out/customer123/token456 → ['customer123', 'token456']
 * - /opt-out/abc/xyz/additional/path → ['abc', 'xyz']
 */
const optOutRegex = /^\/opt-out\/([^/]+)\/([^/]+)(?:\/.*)?$/

/**
 * Matches transactional email opt-out routes.
 *
 * Pattern: /^\/transactional-email-opt-out\/([^/]+)(?:\/.*)?$/
 *
 * Captures:
 * - Group 1: Opt-out identifier/token
 *
 * Example matches:
 * - /transactional-email-opt-out/token123 → ['token123']
 * - /transactional-email-opt-out/abc123/extra/path → ['abc123']
 */
const transactionalEmailOptOutRegex = /^\/transactional-email-opt-out\/([^/]+)(?:\/.*)?$/

/**
 * Matches notification of assignment acknowledgement routes.
 *
 * Pattern: /^\/accounts\/ack-noa(?:.*)?$/
 *
 * Captures: None (no capture groups)
 *
 * Example matches:
 * - /accounts/ack-noa → matches
 * - /accounts/ack-noa/additional/path → matches
 */
const noaRegex = /^\/accounts\/ack-noa(?:.*)?$/

/**
 * Matches grant e-signature consent routes.
 *
 * Pattern: /^\/customers\/([^/]+)\/grant-esign-consent(?:\/.*)?$/
 *
 * Captures:
 * - Group 1: Customer identifier
 *
 * Example matches:
 * - /customers/customer123/grant-esign-consent → ['customer123']
 * - /customers/abc456/grant-esign-consent/success → ['abc456']
 */
const grantEsignRegex = /^\/customers\/([^/]+)\/grant-esign-consent(?:\/.*)?$/

export {
	paymentPlanRegex,
	paymentArrangementRegex,
	secureCodeRegex,
	optOutRegex,
	transactionalEmailOptOutRegex,
	noaRegex,
	grantEsignRegex,
}
